import { z } from "zod";

import { PAY_METHODS, SALE_CHANGE_REASONS, isValidPhone, normalizePhone } from "@pharmacy/domain";

import { isoDate, sizeResponse } from "../catalog/products";
import { moneyNotNegative, moneyPositive, moneyText } from "../money";

/**
 * Sales: the history, recording a sale, and cancelling or editing one.
 *
 * The till's own reads rather than the product list's, for two reasons.
 * Selling needs the Till permission, not Products — an owner can switch
 * Products off for a cashier who should sell without browsing the stock. And
 * the till wants different figures: how much is fresh and how much expired,
 * never what anything cost.
 */

/* ------------------------------------------------------ medicines to sell */

export const tillSearchQuery = z.object({
  /** Which shop's shelf. Stock is counted per branch. */
  branchId: z.string().uuid(),
  q: z.string().trim().max(120).optional(),
  /**
   * These medicines exactly, instead of a search: "a,b,c". For editing a
   * sale, which opens with the medicines it already had.
   */
  ids: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(",").filter(Boolean) : undefined))
    .pipe(z.array(z.string().uuid()).max(100).optional()),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});

export const tillTopQuery = z.object({
  branchId: z.string().uuid(),
});

/**
 * A medicine as the till sees it: its sizes and prices, and what is left of
 * it on this branch's shelf.
 */
export const tillProductResponse = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameSo: z.string().nullable(),
  strength: z.string().nullable(),
  form: z.string().nullable(),
  baseUnit: z.string(),
  /** Smallest first. */
  sizes: z.array(sizeResponse),
  stock: z.object({
    /** Everything, in the smallest unit. Below zero when more was sold than recorded. */
    qtyBase: z.number().int(),
    /** Stock that has not expired. What a sale takes first. */
    sellableBase: z.number().int(),
    /** Stock past its date, still on the shelf. Sold only with a warning. */
    expiredBase: z.number().int(),
    /** When the oldest of that expired stock expired, for the warning. */
    expiredSince: z.string().nullable(),
  }),
});

/**
 * A medicine missing from the app, added from the Add sale page without
 * leaving the sale.
 *
 * Only what the sale needs: its name, what it is counted in, the size it is
 * being sold in and that size's price. No stock — it starts at nothing, the
 * sale takes it below zero, and the stock recorded later in Products pays
 * that back first. Cost, expiry and everything else come with that stock.
 */
export const tillNewProductRequest = z.object({
  /** Made on the phone, so Save tapped twice on a bad connection makes one medicine. */
  id: z.string().uuid(),
  branchId: z.string().uuid(),
  name: z.string().trim().min(2, "Enter the product name").max(160),
  baseUnit: z.string().trim().min(1, "What do you count it in?").max(30),
  /** The size it is sold in. A count of 1 is the smallest unit itself. */
  size: z.object({
    label: z.string().trim().min(1, "Name this size").max(30),
    count: z.number().int().positive("How many does it hold?").max(100_000),
  }),
  /**
   * The sizes' ids, made on the phone. A medicine added with no internet is
   * sold straight away, and that sale has to name its size before the server
   * has ever heard of it.
   */
  sizeIds: z.object({ base: z.string().uuid(), pack: z.string().uuid().optional() }).optional(),
  /** What one of that size sells for. Exact, up to ten decimals. */
  sellPrice: moneyNotNegative("A price cannot be negative"),
});

/* ------------------------------------------------------ recording a sale */

export const saleLineRequest = z.object({
  productId: z.string().uuid(),
  /** The size sold: tablet, strip (10), box (30). */
  unitId: z.string().uuid(),
  qty: z.number().int().positive("How many?").max(100_000),
  /**
   * What one of that size was charged, exactly.
   *
   * The normal price unless it was changed for this customer — lowered or
   * raised, by anybody who can sell.
   */
  price: moneyNotNegative("A price cannot be negative"),
  /**
   * The size's normal price as the phone showed it when the sale was made.
   *
   * A sale made offline was priced from the phone's copy, which may be behind
   * a price changed since on another phone. The sale is recorded against the
   * price its customer was shown, not refused for disagreeing with a price
   * that did not exist when it happened. Without it, today's price is used.
   */
  normalPrice: moneyNotNegative().optional(),
  /**
   * A shop's sale by money: "$1 of sugar" (DukaanBook, 2026-10-27). The money
   * asked for, which is the line's total. Sold in the smallest unit: `price`
   * is that unit's price as shown and `qty` the nearest whole number of units
   * the money buys (the server checks it). Absent on every other line.
   */
  moneyAsked: moneyPositive("Enter an amount").optional(),
});

export const salePaymentRequest = z.object({
  /**
   * `paid`, or `deyn`: on the customer's account, to pay later. A wallet's
   * name from phones still on an older version of the app.
   */
  method: z.enum(PAY_METHODS),
  amount: moneyPositive("Enter an amount"),
});

/**
 * Who bought it, both optional. Typed at the top of the sale, printed on the
 * receipt, and searched for in the history. An empty box is no answer, not
 * an empty name.
 */
const customerName = z
  .string()
  .trim()
  .max(120)
  .nullable()
  .optional()
  .transform((v) => (v ? v : null));

const customerPhone = z
  .string()
  .trim()
  .max(30)
  .nullable()
  .optional()
  .transform((v) => (v ? v : null))
  .refine((v) => v === null || isValidPhone(v), "That is not a valid phone number")
  .transform((v) => (v === null ? null : normalizePhone(v)!));

/**
 * Taken off the whole sale, as an amount — "$4, less $0.50" — never a
 * percentage (the owner, 2026-10-15). Anybody who can sell may give one, up to
 * what the medicines come to. Left out by phones from before it existed.
 */
const saleDiscount = moneyNotNegative("A discount cannot be negative").optional();

export const createSaleRequest = z.object({
  /**
   * Made on the phone, once per sale, and sent again unchanged on a retry.
   *
   * The connection in these shops drops often, and "did that go through?" is
   * answered by tapping Finish again. With the id chosen here, the second tap
   * gets the first sale back instead of recording it twice.
   */
  id: z.string().uuid(),
  branchId: z.string().uuid(),
  lines: z.array(saleLineRequest).min(1, "Add something to sell").max(100),
  saleDiscount,
  /** Empty only for a sale that comes to nothing. */
  payments: z.array(salePaymentRequest).max(PAY_METHODS.length),
  customerName,
  customerPhone,
  /**
   * The phone that made it, and the number it printed: its letter and its own
   * count, "A-0042". Without them the server numbers the sale itself, as it
   * did before phones had letters.
   */
  deviceId: z.string().uuid().optional(),
  receiptNo: z
    .string()
    .regex(/^[A-Z]{1,3}-\d{4,}$/, "Not a receipt number")
    .optional(),
  /**
   * When it was sold, by the phone's clock corrected against the server's. A
   * sale made offline arrives later; this is when it happened.
   */
  soldAt: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), "Not a time")
    .optional(),
  /**
   * Who made the sale. A sale waits on the phone until it can be sent, and by
   * then somebody else may be signed in — the seller signed out, or was
   * signed out. Without this it would be recorded as the sale of whoever sent
   * it. The server only believes it of somebody who has signed in on the
   * phone that made the sale; otherwise the sender is the seller, as before.
   */
  soldBy: z.string().uuid().optional(),
});

/* ------------------------------------------------- cancelling and editing */

/** Why a sale was cancelled or edited. One tap; "other" needs words. */
export const saleChangeReason = z.enum(SALE_CHANGE_REASONS);

const changeWhy = {
  reason: saleChangeReason,
  note: z.string().trim().max(300).nullable().optional(),
  /**
   * When the change was made, by the phone's corrected clock: one made with
   * no internet arrives later, and its time limits — cancel on the sale's own
   * day, edit within seven — are judged by when it was made.
   */
  changedAt: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), "Not a time")
    .optional(),
};

/**
 * A sale, corrected. Everything the sale should now be — the whole of it, not
 * the difference — under a NEW id made on the phone, which is the id the
 * corrected version is recorded under. Sent twice, it is recorded once.
 */
export const editSaleRequest = z.object({
  id: z.string().uuid(),
  lines: z.array(saleLineRequest).min(1, "Add something to sell").max(100),
  saleDiscount,
  payments: z.array(salePaymentRequest).max(PAY_METHODS.length),
  customerName,
  customerPhone,
  ...changeWhy,
});

export const cancelSaleRequest = z.object(changeWhy);

/* ---------------------------------------------------------------- history */

/** The sales history: newest first, a page at a time. */
export const salesListQuery = z.object({
  branchId: z.string().uuid(),
  /** Customer name, phone, receipt number, or a medicine that was sold. */
  q: z.string().trim().max(120).optional(),
  /** One day, in the pharmacy's calendar. */
  date: isoDate.optional(),
  /** Where the previous page ended: `next` from its response. */
  before: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

/* -------------------------------------------------------------- responses */

export const saleLineResponse = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  unitId: z.string().uuid(),
  /** As it was at the moment of sale, with its strength: "Amoxil 500mg". */
  productName: z.string(),
  /** "strip (10)" */
  sizeName: z.string(),
  qty: z.number().int(),
  normalPrice: moneyText,
  /** What one was actually charged. */
  price: moneyText,
  lineTotal: moneyText,
  /** A shop's sale by money: the money asked, which is `lineTotal`. Absent otherwise. */
  moneyAsked: moneyText.optional(),
});

/** What one edit changed on one medicine. Null on the side where it was absent. */
export const saleChangeLine = z.object({
  productName: z.string(),
  sizeName: z.string(),
  before: z.object({ qty: z.number().int(), price: moneyText }).nullable(),
  after: z.object({ qty: z.number().int(), price: moneyText }).nullable(),
});

/** One edit, as the sale's page shows it: who, when, why, and what changed. */
export const saleEditResponse = z.object({
  byName: z.string(),
  at: z.string(),
  reason: saleChangeReason,
  note: z.string().nullable(),
  totalBefore: moneyText,
  totalAfter: moneyText,
  changes: z.array(saleChangeLine),
});

/** A sale, with everything its receipt shows and its page needs. */
export const saleResponse = z.object({
  /** The version that stands. An edited sale answers with its latest version. */
  id: z.string().uuid(),
  /** "0042" */
  receiptNo: z.string(),
  /** `voided` is cancelled. */
  status: z.enum(["completed", "voided", "replaced"]),
  pharmacyName: z.string(),
  branchName: z.string(),
  /** Under the name on the receipt: the branch's phone, else the pharmacy's. Optional: older servers did not send it. */
  pharmacyPhone: z.string().nullable().optional(),
  /** Who rang the sale up in the first place, whoever edited it since. */
  sellerName: z.string(),
  /** ISO timestamp of the sale itself. An edit does not move it. */
  soldAt: z.string(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  lines: z.array(saleLineResponse),
  subtotal: moneyText,
  /** Everything taken off: changed prices and `saleDiscount`. */
  discount: moneyText,
  /** Taken off the whole sale, as an amount. Optional: older servers did not send it. */
  saleDiscount: moneyText.optional(),
  total: moneyText,
  payments: z.array(z.object({ method: z.string(), amount: moneyText })),
  /** What the customer owed just after this sale, when some of it went on credit. */
  balanceAfter: moneyText.nullable(),
  /** Oldest first. */
  edits: z.array(saleEditResponse),
  cancelled: z
    .object({
      byName: z.string(),
      at: z.string(),
      reason: saleChangeReason,
      note: z.string().nullable(),
    })
    .nullable(),
  /** Within 14 days of the sale, in the pharmacy's calendar, and not already cancelled. */
  canCancel: z.boolean(),
  /** Within 30 days of the sale, and not cancelled. */
  canEdit: z.boolean(),
});

/** A row in the sales history. */
export const saleSummaryResponse = z.object({
  id: z.string().uuid(),
  receiptNo: z.string(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  soldAt: z.string(),
  total: moneyText,
  status: z.enum(["completed", "voided"]),
  edited: z.boolean(),
});

export const salesListResponse = z.object({
  sales: z.array(saleSummaryResponse),
  /** Pass as `before` for the next page. Null when there is no more. */
  next: z.string().nullable(),
});

export type TillSearchQuery = z.infer<typeof tillSearchQuery>;
export type TillTopQuery = z.infer<typeof tillTopQuery>;
export type TillProductResponse = z.infer<typeof tillProductResponse>;
export type TillNewProductRequest = z.infer<typeof tillNewProductRequest>;
export type SaleLineRequest = z.infer<typeof saleLineRequest>;
export type SalePaymentRequest = z.infer<typeof salePaymentRequest>;
export type CreateSaleRequest = z.infer<typeof createSaleRequest>;
export type SaleChangeReason = z.infer<typeof saleChangeReason>;
export type EditSaleRequest = z.infer<typeof editSaleRequest>;
export type CancelSaleRequest = z.infer<typeof cancelSaleRequest>;
export type SalesListQuery = z.infer<typeof salesListQuery>;
export type SaleLineResponse = z.infer<typeof saleLineResponse>;
export type SaleChangeLine = z.infer<typeof saleChangeLine>;
export type SaleEditResponse = z.infer<typeof saleEditResponse>;
export type SaleResponse = z.infer<typeof saleResponse>;
export type SaleSummaryResponse = z.infer<typeof saleSummaryResponse>;
export type SalesListResponse = z.infer<typeof salesListResponse>;
